import { useState, useEffect, useMemo } from 'react';
import { showAlert } from '@/components/AppDialog';
import { API_BASE_URL } from '@/utils/env';
import { getFreshToken } from '@/utils/auth';
import { BRAND } from '@/brand';
import AdminSidebar from '../components/AdminSidebar';
import AdminProfileMenu from '../components/AdminProfileMenu';
import { Save, CheckCircle2, Trash2, Plus, Inbox, UserPlus, RefreshCw, Download, ExternalLink, AlertTriangle, ChevronDown } from 'lucide-react';

/* Admin — Conncct Private. The console for the public /private page, which runs on
   BOTH brand domains (connectcorporate.com + conncct.com) and promises every
   applicant "a partner will reply by <next working day>" plus a CP-YYYY-NNNN
   reference. This page is built around exactly that:

   - Applications: /private posts into the shared leads store (demoRequests) tagged
     `helpWith: "Conncct Private — …"`. POST /demo/submit only persists six fields,
     so the page packs reference, originating brand/site, location, stage and phone
     into discussionTopic — parseTopic() unpacks them back into real columns.
   - SLA: dueBy() reproduces the page's own promise (next working day), so an
     application still sitting on `new` past its deadline is flagged overdue.
   - VIP register: `{ formFields, members }` stored server-side (RTDB `vip_current`)
     via GET/POST /auth/admin/vip. `formFields` is NOT rendered — the public form is
     hardcoded in ConncctPrivate.tsx, so the old editor controlled nothing; the
     stored value is passed through untouched on save rather than dropped.
   Statuses PATCH instantly (/auth/admin/leads/:id); the register needs Save. */

const IS_CORP = BRAND === 'corporate';

type FieldDef = { id: string; label: string; type: string; required: boolean };
type VipMember = { id: string; name: string; email: string; tier: string; status: string; note: string };
type VipDoc = { formFields: FieldDef[]; members: VipMember[] };
type VipApp = { id: string; firstName?: string; lastName?: string; email?: string; companyName?: string; helpWith?: string; discussionTopic?: string; submittedAt?: string; status?: string };

const DEFAULT_VIP: VipDoc = {
  formFields: [
    { id: 'f-name', label: 'Full name', type: 'text', required: true },
    { id: 'f-email', label: 'Email', type: 'email', required: true },
    { id: 'f-phone', label: 'Phone', type: 'phone', required: false },
    { id: 'f-company', label: 'Company / family office', type: 'text', required: false },
    { id: 'f-interest', label: 'Area of interest', type: 'select', required: false },
    { id: 'f-notes', label: 'Notes', type: 'textarea', required: false },
  ],
  members: [],
};

const TIERS = ['Private', 'Family office', 'Priority', 'Prospect'];
const MEMBER_STATUSES = ['active', 'pending', 'paused'];
const APP_STATUSES = ['new', 'contacted', 'scheduled', 'completed', 'dismissed'];
const STATUS_LABEL: Record<string, string> = { new: 'New', pending: 'New', contacted: 'Contacted', scheduled: 'Scheduled', completed: 'Completed', dismissed: 'Dismissed' };

const isPrivateApp = (l: VipApp) => /conncct\s*private/i.test(l.helpWith || '');
const appName = (l: VipApp) => `${l.firstName || ''} ${l.lastName || ''}`.replace(/\s*—\s*$/, '').trim() || l.email || 'Enquiry';
const appServices = (l: VipApp) => (l.helpWith || '').replace(/^conncct private\s*[—-]\s*/i, '').trim();
const norm = (s?: string) => { const v = s === 'pending' ? 'new' : (s || 'new'); return APP_STATUSES.includes(v) ? v : 'new'; };

/* /private packs its extra answers into discussionTopic as " · "-joined
   "Key: value" segments; anything without a known key is the applicant's own
   message. Older submissions (before Ref/Brand/Site were added) simply come back
   with those fields empty. */
const KEYS = /^(Ref|Brand|Site|Based|Stage|Phone):\s*(.*)$/i;
type Parsed = { message: string; ref: string; brand: string; site: string; based: string; stage: string; phone: string };
const parseTopic = (t?: string): Parsed => {
  const out: Parsed = { message: '', ref: '', brand: '', site: '', based: '', stage: '', phone: '' };
  (t || '').split(' · ').forEach(seg => {
    const s = seg.trim();
    if (!s) return;
    const m = KEYS.exec(s);
    if (m && m[1]) {
      const k = m[1].toLowerCase() as keyof Parsed;
      out[k] = (m[2] || '').trim();
    } else {
      out.message = out.message ? `${out.message} · ${s}` : s;
    }
  });
  return out;
};

/* The public page tells every applicant a partner replies by the next working
   day — so that same date is the SLA the console holds itself to. */
const dueBy = (iso?: string) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  d.setHours(23, 59, 59, 999);
  return d;
};
const isOverdue = (a: VipApp) => {
  if (norm(a.status) !== 'new') return false;
  const due = dueBy(a.submittedAt);
  return !!due && Date.now() > due.getTime();
};
const ageOf = (iso?: string) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return '—';
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
};
const fmtDate = (v?: string) => { if (!v) return '—'; try { return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return v; } };
const initials = (l: VipApp) => {
  const f = (l.firstName || '').trim(), ln = (l.lastName || '').replace(/—/g, '').trim();
  if (f || ln) return `${f[0] || ''}${ln[0] || ''}`.toUpperCase();
  return (l.email || '?').slice(0, 2).toUpperCase();
};
const AV_COLORS = ['#DBEAFE|#1D4ED8', '#EEF2F6|#475569', '#E9F7EF|#15803D', '#FEF6E7|#B45309', '#FDF2F8|#BE185D'];
const avStyle = (key: string) => {
  const sum = [...key].reduce((a, c) => a + c.charCodeAt(0), 0);
  const [background, color] = (AV_COLORS[sum % AV_COLORS.length] || AV_COLORS[0]!).split('|');
  return { background, color };
};

const CHEV = "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%2364748B' stroke-width='1.5'/%3E%3C/svg%3E\")";

const CSS = `
.adminx{font-family:'Poppins',system-ui,sans-serif;color:#0F172A}
.adminx .lead-top{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:16px}
.adminx .lead-top>div{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap}
.adminx .lead-top b{font-size:17px;font-weight:700;letter-spacing:-.01em}
.adminx .hint{font-size:11.5px;color:#94A3B8}
.adminx .top-actions{margin-left:auto;display:flex;gap:8px;flex-wrap:wrap}
.adminx .btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;border:1px solid #E6E9ED;background:#fff;border-radius:9px;padding:9px 15px;font-size:13px;font-weight:600;color:#334155;font-family:inherit;cursor:pointer;transition:.14s;white-space:nowrap;text-decoration:none}
.adminx .btn:hover{border-color:#D5DAE1;background:#FAFBFC}
.adminx .btn:disabled{opacity:.45;cursor:not-allowed}
.adminx .btn svg{width:14px;height:14px}
.adminx .btn-pri{background:var(--brand);border-color:var(--brand);color:#fff}
.adminx .btn-pri:hover{background:var(--brand-d);border-color:var(--brand-d)}
.adminx .btn-xs{padding:6px 10px;font-size:12px;border-radius:8px}
@keyframes axspin{to{transform:rotate(360deg)}}
.adminx .spin{animation:axspin 1s linear infinite}
.adminx .card{background:#fff;border:1px solid #E6E9ED;border-radius:14px;box-shadow:0 1px 2px rgba(15,23,42,.05)}
.adminx .card-b{padding:20px}
.adminx .section-gap{margin-bottom:20px}
.adminx .sec-h{display:flex;align-items:center;gap:8px;font-size:11px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:#64748B;margin-bottom:14px}
.adminx .sec-h svg{width:14px;height:14px}
.adminx .sec-h .right{margin-left:auto;display:flex;gap:8px;align-items:center}

/* KPI tiles — the numbers a partner actually needs off this page. */
.adminx .kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-bottom:20px}
@media(max-width:1080px){.adminx .kpis{grid-template-columns:repeat(3,1fr)}}
@media(max-width:640px){.adminx .kpis{grid-template-columns:repeat(2,1fr)}}
.adminx .kpi{background:#fff;border:1px solid #E6E9ED;border-radius:14px;padding:15px 16px;box-shadow:0 1px 2px rgba(15,23,42,.05)}
.adminx .kpi span{display:block;font-size:11px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:#94A3B8}
.adminx .kpi b{display:block;font-size:26px;font-weight:700;letter-spacing:-.02em;margin-top:6px;line-height:1}
.adminx .kpi em{font-style:normal;font-size:11.5px;color:#94A3B8;display:block;margin-top:5px}
.adminx .kpi.warn{border-color:#FECACA;background:#FEF7F7}
.adminx .kpi.warn b{color:#B91C1C}

/* Pipeline strip doubles as the status filter. */
.adminx .pipe{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px}
.adminx .pipe button{border:1px solid #E6E9ED;background:#fff;border-radius:999px;padding:7px 14px;font:inherit;font-size:12.5px;font-weight:600;color:#64748B;cursor:pointer;transition:.14s;display:inline-flex;align-items:center;gap:7px}
.adminx .pipe button:hover{border-color:#D5DAE1}
.adminx .pipe button.on{border-color:var(--brand);color:var(--brand-d);background:var(--brand-soft)}
.adminx .pipe button i{font-style:normal;font-size:11px;background:#EFF1F4;color:#64748B;border-radius:999px;padding:1px 7px;font-weight:700}
.adminx .pipe button.on i{background:#fff;color:var(--brand-d)}

.adminx .qf-row{display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap}
.adminx .qf-actions{margin-left:auto;display:flex;gap:8px}
.adminx .fld{display:flex;flex-direction:column;gap:6px;min-width:0}
.adminx .fld label{font-size:11px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:#64748B}
.adminx .fld input,.adminx .fld select,.adminx .fld textarea{border:1px solid #E6E9ED;border-radius:9px;padding:9px 12px;font-size:13px;background:#fff;width:100%;font-family:inherit;color:#0F172A}
.adminx .fld select{-webkit-appearance:none;appearance:none;padding-right:30px;background-image:${CHEV};background-repeat:no-repeat;background-position:right 11px center}
.adminx .fld input:focus,.adminx .fld select:focus,.adminx .fld textarea:focus{border-color:var(--brand);outline:none;box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .fld input::placeholder{color:#94A3B8}

.adminx .tblwrap{overflow-x:auto;border-radius:14px}
.adminx .tbl{width:100%;border-collapse:collapse}
.adminx .tbl th{font-size:11px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:#64748B;text-align:left;padding:10px 16px;background:#FBFCFD;border-bottom:1px solid #EFF1F4;white-space:nowrap}
.adminx .tbl td{font-size:13px;padding:12px 16px;border-bottom:1px solid #EFF1F4;vertical-align:middle;color:#475569}
.adminx .tbl tbody tr:hover{background:#FCFDFE}
.adminx .tbl tbody tr.open{background:#FBFCFD}
.adminx .tbl tbody tr.det td{border-bottom:1px solid #EFF1F4;background:#FBFCFD}
.adminx .cell-person{display:flex;align-items:center;gap:10px;min-width:0}
.adminx .cell-person .avatar{width:34px;height:34px;border-radius:50%;display:grid;place-items:center;font-size:12px;font-weight:600;flex:none}
.adminx .cell-person b{display:block;font-size:13px;font-weight:600;color:#0F172A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.adminx .cell-person a{display:block;font-size:11.5px;color:#94A3B8;text-decoration:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:230px}
.adminx .cell-person a:hover{color:var(--brand);text-decoration:underline}
.adminx .muted{color:#CBD5E1}
.adminx .company{color:#334155;font-weight:500;white-space:nowrap}
.adminx .refchip{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11.5px;font-weight:600;color:#475569;background:#F1F5F9;border-radius:6px;padding:3px 7px;white-space:nowrap}
.adminx .pill{display:inline-flex;align-items:center;font-size:11.5px;font-weight:600;padding:4px 10px;border-radius:999px;white-space:nowrap}
.adminx .pill.plain{background:#EFF1F4;color:#64748B;margin:2px 4px 2px 0}
.adminx .pill.corp{background:#FEF3C7;color:#B45309}
.adminx .pill.cons{background:#DBEAFE;color:#1D4ED8}
.adminx .pill.od{background:#FEE2E2;color:#B91C1C;gap:5px}
.adminx .pill.od svg{width:12px;height:12px}
.adminx .when{color:#94A3B8;font-weight:500;white-space:nowrap}
.adminx .when b{display:block;color:#475569;font-weight:600;font-size:12.5px}
.adminx .status-select{-webkit-appearance:none;appearance:none;border:1px solid #E6E9ED;border-radius:999px;padding:5px 26px 5px 12px;font:inherit;font-size:12px;font-weight:600;cursor:pointer;background-image:${CHEV};background-repeat:no-repeat;background-position:right 10px center;transition:.14s;color:#334155}
.adminx .status-select:focus{outline:none;box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .status-select:disabled{opacity:.6;cursor:default}
.adminx .rowacts{display:flex;gap:6px;align-items:center;justify-content:flex-end}
.adminx .iconbtn{border:1px solid #E6E9ED;background:#fff;border-radius:8px;width:30px;height:30px;display:grid;place-items:center;cursor:pointer;color:#64748B;transition:.14s;flex:none}
.adminx .iconbtn:hover{border-color:var(--brand);color:var(--brand-d)}
.adminx .iconbtn svg{width:14px;height:14px}
.adminx .iconbtn.rot svg{transform:rotate(180deg)}

/* Expanded application — everything the applicant actually told us. */
.adminx .det-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px 18px;margin-bottom:14px}
@media(max-width:860px){.adminx .det-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
.adminx .det-grid div span{display:block;font-size:10.5px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:#94A3B8;margin-bottom:3px}
.adminx .det-grid div b{font-size:13px;font-weight:600;color:#334155;word-break:break-word}
.adminx .msg{background:#fff;border:1px solid #EFF1F4;border-radius:10px;padding:12px 14px;font-size:13px;color:#334155;line-height:1.65;white-space:pre-wrap}
.adminx .det-acts{display:flex;gap:8px;flex-wrap:wrap;margin-top:14px}
.adminx .empty{text-align:center;padding:34px 12px;color:#94A3B8;font-size:13px}
.adminx .empty b{display:block;color:#475569;font-size:14px;margin-bottom:4px}

.adminx .mem{border:1px solid #EFF1F4;border-radius:12px;padding:14px;margin-bottom:10px}
.adminx .mem-grid{display:grid;grid-template-columns:1fr 1fr 150px 130px 34px;gap:10px;align-items:end}
@media(max-width:900px){.adminx .mem-grid{grid-template-columns:1fr 1fr}}
.adminx .del{border:none;background:none;color:#EF4444;cursor:pointer;padding:8px 0}
.adminx .del svg{width:15px;height:15px}
.adminx .saved{display:inline-flex;align-items:center;gap:6px;color:#15803D;font-weight:600;font-size:12.5px}
.adminx .saved svg{width:15px;height:15px}
`;

export default function AdminVIP() {
  const [m, setM] = useState<VipDoc>(DEFAULT_VIP);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [apps, setApps] = useState<VipApp[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [busyApp, setBusyApp] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  // Filters
  const [status, setStatus] = useState('all');
  const [source, setSource] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');

  useEffect(() => {
    (async () => {
      try {
        const token = await getFreshToken();
        const res = await fetch(`${API_BASE_URL}/auth/admin/vip`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
        const json = await res.json();
        const d = (json && json.vip) as Partial<VipDoc> | null;
        if (d) {
          setM({
            formFields: Array.isArray(d.formFields) && d.formFields.length ? d.formFields : DEFAULT_VIP.formFields,
            members: Array.isArray(d.members) ? d.members : [],
          });
        }
      } catch (e) { console.error('Failed to load VIP config:', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadApps = async () => {
    setAppsLoading(true);
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/leads`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      const json = await res.json();
      if (res.ok && json.success) setApps((json.demoRequests || []).filter(isPrivateApp));
    } catch (e) { console.error('Failed to load applications:', e); }
    finally { setAppsLoading(false); }
  };
  useEffect(() => { loadApps(); }, []);

  const setAppStatus = async (app: VipApp, next: string) => {
    setApps(prev => prev.map(a => a.id === app.id ? { ...a, status: next } : a)); // optimistic
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/leads/${app.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error();
    } catch { loadApps(); }
  };

  const mutate = (fn: (d: VipDoc) => void) => { setM(prev => { const next: VipDoc = JSON.parse(JSON.stringify(prev)); fn(next); return next; }); setSavedAt(null); };

  // Promote an application into the VIP register (Save persists it).
  const addAsMember = (app: VipApp) => {
    const p = parseTopic(app.discussionTopic);
    setBusyApp(app.id);
    mutate(d => {
      if (d.members.some(v => v.email && app.email && v.email.toLowerCase() === app.email.toLowerCase())) return;
      d.members.unshift({
        id: 'm' + Date.now(),
        name: appName(app),
        email: app.email || '',
        tier: 'Prospect',
        status: 'pending',
        note: [p.ref, appServices(app), p.stage].filter(Boolean).join(' · '),
      });
    });
    setAppStatus(app, 'contacted');
    setTimeout(() => setBusyApp(null), 400);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/vip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        // formFields is passed through untouched — nothing here edits it.
        body: JSON.stringify({ formFields: m.formFields, members: m.members }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw new Error(json?.error || `Save failed (${res.status})`);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.message || 'Save failed — please try again.');
    } finally { setSaving(false); }
  };

  // Parse once per load, not per render pass.
  const rows = useMemo(() => apps.map(a => ({ a, p: parseTopic(a.discussionTopic), od: isOverdue(a) })), [apps]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length, new: 0, contacted: 0, scheduled: 0, completed: 0, dismissed: 0 };
    rows.forEach(r => { const s = norm(r.a.status); c[s] = (c[s] || 0) + 1; });
    return c;
  }, [rows]);

  const overdueCount = useMemo(() => rows.filter(r => r.od).length, [rows]);
  const weekCount = useMemo(() => {
    const cut = Date.now() - 7 * 86400000;
    return rows.filter(r => r.a.submittedAt && new Date(r.a.submittedAt).getTime() >= cut).length;
  }, [rows]);

  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter(r => {
      if (status !== 'all' && norm(r.a.status) !== status) return false;
      if (source !== 'all') {
        const b = (r.p.brand || '').toLowerCase();
        if (source === 'unknown' ? !!b : b !== source) return false;
      }
      if (!needle) return true;
      return [appName(r.a), r.a.email, r.a.companyName, appServices(r.a), r.p.ref, r.p.stage, r.p.based, r.p.message, r.p.site]
        .some(v => (v || '').toLowerCase().includes(needle));
    });
    const ts = (r: typeof out[number]) => new Date(r.a.submittedAt || 0).getTime();
    if (sort === 'oldest') out = [...out].sort((x, y) => ts(x) - ts(y));
    else if (sort === 'overdue') out = [...out].sort((x, y) => (Number(y.od) - Number(x.od)) || (ts(y) - ts(x)));
    else out = [...out].sort((x, y) => ts(y) - ts(x));
    return out;
  }, [rows, status, source, q, sort]);

  const exportCsv = () => {
    const head = ['Reference', 'Received', 'Name', 'Email', 'Company', 'Brand', 'Site', 'Needs', 'Stage', 'Based', 'Phone', 'Status', 'Overdue', 'Message'];
    const body = view.map(({ a, p, od }) => [
      p.ref, a.submittedAt || '', appName(a), a.email || '', a.companyName || '', p.brand, p.site,
      appServices(a), p.stage, p.based, p.phone, STATUS_LABEL[norm(a.status)] || '', od ? 'yes' : 'no', p.message,
    ]);
    const csv = [head, ...body].map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `conncct-private-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Partner reply, pre-addressed and pre-referenced so the applicant's quoted ref matches.
  const replyHref = (a: VipApp, p: Parsed) => {
    const subject = `Conncct Private — your enquiry${p.ref ? ` (${p.ref})` : ''}`;
    const body = `Dear ${(a.firstName || '').trim() || 'there'},\n\nThank you for your enquiry to Conncct Private${p.ref ? ` (reference ${p.ref})` : ''}.\n\n`;
    return `mailto:${a.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F4F6F8] flex"><AdminSidebar />
      <div className="flex-1 xl:ml-64 flex items-center justify-center"><div className="text-gray-400 font-medium">Loading Conncct Private…</div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <AdminSidebar />
      <div className="flex-1 xl:ml-64 overflow-x-hidden adminx">
        <style>{CSS}</style>
        <header className="bg-white border-b border-[#E6E9ED] px-4 md:px-6 h-[58px] flex items-center justify-between sticky top-0 z-30">
          <span className="text-[13px] text-[#64748B] font-medium ml-10 xl:ml-0">Admin / <b className="text-[#0F172A] font-semibold">Conncct Private</b></span>
          <AdminProfileMenu />
        </header>

        <div className="page">
          <div className="lead-top">
            <div>
              <b>Conncct Private</b>
              <span className="hint">Mandate applications from /private — on both brand sites. A partner replies by the next working day.</span>
            </div>
            <div className="top-actions">
              <a className="btn" href="/private" target="_blank" rel="noreferrer"><ExternalLink /> Public page</a>
              <button className="btn" onClick={exportCsv} disabled={!view.length}><Download /> Export CSV</button>
              <button className="btn btn-pri" onClick={loadApps} disabled={appsLoading}><RefreshCw className={appsLoading ? 'spin' : ''} /> Refresh</button>
            </div>
          </div>

          <div className="kpis">
            <div className="kpi"><span>Applications</span><b>{counts.all}</b><em>{weekCount} in the last 7 days</em></div>
            <div className="kpi"><span>Awaiting a partner</span><b>{counts.new || 0}</b><em>status still “new”</em></div>
            <div className={`kpi${overdueCount ? ' warn' : ''}`}><span>Past SLA</span><b>{overdueCount}</b><em>{overdueCount ? 'reply promised by now' : 'all within promise'}</em></div>
            <div className="kpi"><span>In conversation</span><b>{(counts.contacted || 0) + (counts.scheduled || 0)}</b><em>contacted or scheduled</em></div>
            <div className="kpi"><span>Converted</span><b>{counts.completed || 0}</b><em>{counts.all ? Math.round(((counts.completed || 0) / counts.all) * 100) : 0}% of all applications</em></div>
          </div>

          {/* Pipeline / status filter */}
          <div className="pipe">
            {['all', ...APP_STATUSES].map(s => (
              <button key={s} className={status === s ? 'on' : ''} onClick={() => setStatus(s)}>
                {s === 'all' ? 'All' : STATUS_LABEL[s]} <i>{counts[s] || 0}</i>
              </button>
            ))}
          </div>

          <div className="card section-gap">
            <div className="card-b">
              <div className="qf-row">
                <div className="fld" style={{ flex: '1 1 260px' }}>
                  <label>Search</label>
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Name, email, company, reference, need…" />
                </div>
                <div className="fld" style={{ width: 190 }}>
                  <label>Came from</label>
                  <select value={source} onChange={e => setSource(e.target.value)}>
                    <option value="all">Both sites</option>
                    <option value="consulting">Consulting · conncct.com</option>
                    <option value="corporate">Corporate · connectcorporate.com</option>
                    <option value="unknown">Not recorded</option>
                  </select>
                </div>
                <div className="fld" style={{ width: 170 }}>
                  <label>Sort</label>
                  <select value={sort} onChange={e => setSort(e.target.value)}>
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="overdue">Past SLA first</option>
                  </select>
                </div>
                <div className="qf-actions">
                  <button className="btn" onClick={() => { setQ(''); setSource('all'); setStatus('all'); setSort('newest'); }}>Reset</button>
                </div>
              </div>
            </div>
          </div>

          <div className="card section-gap">
            <div className="card-b" style={{ paddingBottom: 8 }}>
              <div className="sec-h"><Inbox /> Applications <span className="right hint">{view.length} shown of {counts.all}</span></div>
            </div>
            {appsLoading ? (
              <div className="empty">Loading applications…</div>
            ) : !view.length ? (
              <div className="empty">
                <b>{counts.all ? 'Nothing matches these filters' : 'No applications yet'}</b>
                {counts.all ? 'Try clearing the search or status filter.' : 'Enquiries submitted on the public Conncct Private page land here automatically.'}
              </div>
            ) : (
              <div className="tblwrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Applicant</th><th>Company</th><th>Reference</th><th>From</th>
                      <th>Needs</th><th>Received</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.map(({ a, p, od }) => {
                      const open = openId === a.id;
                      const svc = appServices(a);
                      return [
                        <tr key={a.id} className={open ? 'open' : ''}>
                          <td>
                            <div className="cell-person">
                              <span className="avatar" style={avStyle(appName(a))}>{initials(a)}</span>
                              <div style={{ minWidth: 0 }}>
                                <b>{appName(a)}</b>
                                {a.email ? <a href={`mailto:${a.email}`}>{a.email}</a> : <span className="muted">—</span>}
                              </div>
                            </div>
                          </td>
                          <td>{a.companyName ? <span className="company">{a.companyName}</span> : <span className="muted">—</span>}</td>
                          <td>{p.ref ? <span className="refchip">{p.ref}</span> : <span className="muted">—</span>}</td>
                          <td>
                            {p.brand
                              ? <span className={`pill ${p.brand.toLowerCase() === 'corporate' ? 'corp' : 'cons'}`}>{p.brand}</span>
                              : <span className="muted">—</span>}
                          </td>
                          <td style={{ maxWidth: 240 }}>
                            {svc ? svc.split(',').map(s => <span key={s} className="pill plain">{s.trim()}</span>) : <span className="muted">—</span>}
                          </td>
                          <td>
                            <span className="when"><b>{ageOf(a.submittedAt)}</b>{fmtDate(a.submittedAt)}</span>
                            {od && <div style={{ marginTop: 4 }}><span className="pill od"><AlertTriangle /> Past SLA</span></div>}
                          </td>
                          <td>
                            <select className="status-select" value={norm(a.status)} onChange={e => setAppStatus(a, e.target.value)}>
                              {APP_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                            </select>
                          </td>
                          <td>
                            <div className="rowacts">
                              <button className="iconbtn" title="Add to the VIP register" disabled={busyApp === a.id} onClick={() => addAsMember(a)}><UserPlus /></button>
                              <button className={`iconbtn${open ? ' rot' : ''}`} title={open ? 'Collapse' : 'Full enquiry'} onClick={() => setOpenId(open ? null : a.id)}><ChevronDown /></button>
                            </div>
                          </td>
                        </tr>,
                        open ? (
                          <tr key={a.id + '-d'} className="det">
                            <td colSpan={8}>
                              <div className="det-grid">
                                <div><span>Stage</span><b>{p.stage || '—'}</b></div>
                                <div><span>Based</span><b>{p.based || '—'}</b></div>
                                <div><span>Phone / WhatsApp</span><b>{p.phone || '—'}</b></div>
                                <div><span>Submitted from</span><b>{p.site || '—'}</b></div>
                                <div><span>Reference</span><b>{p.ref || 'not recorded'}</b></div>
                                <div><span>Reply promised by</span><b>{dueBy(a.submittedAt)?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }) || '—'}</b></div>
                                <div><span>Needs</span><b>{svc || '—'}</b></div>
                                <div><span>Status</span><b>{STATUS_LABEL[norm(a.status)]}</b></div>
                              </div>
                              <div className="msg">{p.message || 'No message left.'}</div>
                              <div className="det-acts">
                                <a className="btn btn-pri btn-xs" href={replyHref(a, p)}>Reply as a partner</a>
                                {a.email && <button className="btn btn-xs" onClick={() => { navigator.clipboard?.writeText(a.email || ''); }}>Copy email</button>}
                                <button className="btn btn-xs" onClick={() => setAppStatus(a, 'scheduled')}>Mark scheduled</button>
                                <button className="btn btn-xs" onClick={() => setAppStatus(a, 'completed')}>Mark converted</button>
                                <button className="btn btn-xs" onClick={() => setAppStatus(a, 'dismissed')}>Dismiss</button>
                              </div>
                            </td>
                          </tr>
                        ) : null,
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* VIP register */}
          <div className="card section-gap">
            <div className="card-b">
              <div className="sec-h">
                VIP register ({m.members.length})
                <span className="right">
                  <button className="btn btn-xs" onClick={() => mutate(d => { d.members.unshift({ id: 'm' + Date.now(), name: '', email: '', tier: 'Private', status: 'active', note: '' }); })}><Plus /> Add member</button>
                </span>
              </div>
              {!m.members.length && <div className="empty" style={{ padding: '22px 12px' }}>No VIP members yet. Promote an application with the <b style={{ display: 'inline' }}>person+</b> action above.</div>}
              {m.members.map((v, i) => (
                <div className="mem" key={v.id || i}>
                  <div className="mem-grid">
                    <div className="fld"><label>Name</label><input value={v.name} onChange={e => mutate(d => { d.members[i]!.name = e.target.value; })} /></div>
                    <div className="fld"><label>Email</label><input value={v.email} onChange={e => mutate(d => { d.members[i]!.email = e.target.value; })} /></div>
                    <div className="fld"><label>Tier</label><select value={v.tier} onChange={e => mutate(d => { d.members[i]!.tier = e.target.value; })}>{TIERS.map(t => <option key={t}>{t}</option>)}</select></div>
                    <div className="fld"><label>Status</label><select value={v.status} onChange={e => mutate(d => { d.members[i]!.status = e.target.value; })}>{MEMBER_STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
                    <button className="del" title="Remove" onClick={() => mutate(d => { d.members.splice(i, 1); })}><Trash2 /></button>
                  </div>
                  <div className="fld" style={{ marginTop: 10 }}><label>Note</label><input value={v.note} onChange={e => mutate(d => { d.members[i]!.note = e.target.value; })} placeholder="Reference · what they need · stage" /></div>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
                <button className="btn btn-pri" onClick={handleSave} disabled={saving}><Save />{saving ? 'Saving…' : 'Save register'}</button>
                {savedAt && <span className="saved"><CheckCircle2 /> Saved · {savedAt}</span>}
                <span className="hint">Application statuses save instantly; the register needs Save.</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
